import { useEffect, useState } from 'react';
import { Button, Group, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { EdoSubscriberField } from '../../shared/ui/EdoSubscriberField';
import {
  AddressBreakdownFields,
  useAddressBreakdown,
} from '../../shared/ui/AddressBreakdownFields';
import {
  ENGAGED_PARTY_ROLE_LABEL,
  VAT_RATE_OPTIONS,
  type CarrierCreateInput,
  type CarrierDto,
  type CarrierPatchInput,
  type ClientType,
  type EngagedPartyRole,
} from '@expeditor/shared';
import { carriersApi } from '../../shared/api/shipments';
import { ReferenceBookTable } from '../client-cabinet/ReferenceBookTable';

const TYPE_LABEL: Record<ClientType, string> = {
  INDIVIDUAL: 'ФЛ',
  ENTREPRENEUR: 'ИП',
  LEGAL_RU: 'ЮЛ РФ',
  LEGAL_FOREIGN: 'Иностранная',
};

export function CarriersPage() {
  return (
    <ReferenceBookTable<CarrierDto, CarrierCreateInput, CarrierPatchInput>
      title="Привлекаемые исполнители"
      api={carriersApi}
      columns={[
        { header: 'Роль', render: (i) => ENGAGED_PARTY_ROLE_LABEL[i.role] },
        { header: 'Тип', render: (i) => TYPE_LABEL[i.type] },
        { header: 'Наименование', render: (i) => i.fullName },
        { header: 'Краткое', render: (i) => i.shortName ?? '—' },
        { header: 'ИНН', render: (i) => i.inn ?? '—' },
        { header: 'Адрес', render: (i) => i.address ?? '—' },
        { header: 'Контакт', render: (i) => i.contactPhone ?? i.contactEmail ?? '—' },
      ]}
      renderForm={CarrierForm}
    />
  );
}

function CarrierForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial: CarrierDto | null;
  onSubmit: (dto: CarrierCreateInput | CarrierPatchInput) => Promise<void>;
  submitting: boolean;
}) {
  const [role, setRole] = useState<EngagedPartyRole>(initial?.role ?? 'CARRIER');
  const [type, setType] = useState<ClientType>(initial?.type ?? 'LEGAL_RU');
  const [fullName, setFullName] = useState(initial?.fullName ?? '');
  const [shortName, setShortName] = useState(initial?.shortName ?? '');
  const [inn, setInn] = useState(initial?.inn ?? '');
  // Адрес и его разбивка (АдрРФ) — идут в стороны ЭТрН.
  const [address, setAddress] = useState(initial?.address ?? '');
  const { parts, setParts, save: saveParts } = useAddressBreakdown(initial?.address);
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '');
  // Код абонента ЭДО (МИГ24) — адрес лица для электронных документов.
  const [edoSubscriberId, setEdoSubscriberId] = useState(initial?.edoSubscriberId ?? '');
  const [vatRate, setVatRate] = useState<string | null>(initial?.vatRate ?? null);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  // Синхронизация state с initial — useState инициализируется только при первом
  // монтировании, но Mantine Modal сохраняет компонент в DOM при закрытии,
  // поэтому без useEffect новые initial не подхватятся.
  useEffect(() => {
    setRole(initial?.role ?? 'CARRIER');
    setType(initial?.type ?? 'LEGAL_RU');
    setFullName(initial?.fullName ?? '');
    setShortName(initial?.shortName ?? '');
    setInn(initial?.inn ?? '');
    setAddress(initial?.address ?? '');
    setContactPhone(initial?.contactPhone ?? '');
    setContactEmail(initial?.contactEmail ?? '');
    setEdoSubscriberId(initial?.edoSubscriberId ?? '');
    setVatRate(initial?.vatRate ?? null);
    setNotes(initial?.notes ?? '');
  }, [initial?.id]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Разбивка живёт отдельно (одна на текст адреса) — сохраняем её рядом с карточкой.
    void saveParts(address).catch(() => undefined);
    void onSubmit({
      role,
      type,
      fullName,
      shortName: shortName || undefined,
      inn: inn || undefined,
      address: address || undefined,
      contactPhone: contactPhone || undefined,
      contactEmail: contactEmail || undefined,
      edoSubscriberId: edoSubscriberId || undefined,
      vatRate: vatRate ?? undefined,
      notes: notes || undefined,
    });
  };

  return (
    <form onSubmit={submit}>
      <Stack>
        <Select
          label="Роль"
          data={Object.entries(ENGAGED_PARTY_ROLE_LABEL).map(([v, l]) => ({ value: v, label: l }))}
          value={role}
          onChange={(v) => v && setRole(v as EngagedPartyRole)}
          required
        />
        <Select
          label="Тип"
          data={Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l }))}
          value={type}
          onChange={(v) => v && setType(v as ClientType)}
          required
        />
        <TextInput
          label="Полное наименование"
          required
          value={fullName}
          onChange={(e) => setFullName(e.currentTarget.value)}
        />
        <TextInput
          label="Краткое наименование"
          value={shortName}
          onChange={(e) => setShortName(e.currentTarget.value)}
        />
        <Group grow>
          <TextInput
            label="ИНН"
            value={inn}
            onChange={(e) => setInn(e.currentTarget.value)}
          />
          <TextInput
            label="Контактный телефон"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.currentTarget.value)}
          />
        </Group>
        <TextInput
          label="Email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.currentTarget.value)}
        />
        <TextInput
          label="Адрес"
          description="Юридический (почтовый) адрес — подставляется в стороны ЭТрН"
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
        <AddressBreakdownFields
          value={parts}
          onChange={setParts}
          hint="Нужна для ЭТрН: адрес уходит в XML структурированно (АдрРФ). Хранится по тексту адреса."
        />
        <EdoSubscriberField value={edoSubscriberId} onChange={setEdoSubscriberId} inn={inn} />
        <Select
          label="Ставка НДС"
          description="По какой ставке работает исполнитель — подставится в стоимость при создании ЭТрН"
          data={VAT_RATE_OPTIONS}
          value={vatRate}
          onChange={setVatRate}
          clearable
          placeholder="не указана"
        />
        <Textarea
          label="Заметки"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />
        <Button type="submit" loading={submitting}>
          Сохранить
        </Button>
      </Stack>
    </form>
  );
}
